import { useEffect, useMemo, useRef, useState } from 'react'
import { legalSections, products, productOptions, serviceItems } from './data.js'
import { resolveRoute } from './routes.js'

function currentPath() {
  return window.location.pathname.replace(/\/+$/, '') || '/'
}

function Arrow({ dark = false }) {
  return <span aria-hidden="true" className={dark ? 'arrow arrow--dark' : 'arrow'}>↘</span>
}

function ButtonLink({ href, children, light = false, className = '' }) {
  return <a className={`pill ${light ? 'pill--light' : ''} ${className}`} href={href}>{children}<Arrow dark={light} /></a>
}

function Header({ overlay = false, active = '' }) {
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [hidden, setHidden] = useState(false)
  const previousScrollY = useRef(0)
  useEffect(() => {
    document.body.classList.toggle('menu-open', open)
    return () => document.body.classList.remove('menu-open')
  }, [open])
  useEffect(() => {
    const handleScroll = () => {
      const nextScrollY = window.scrollY
      setScrolled(nextScrollY > 20)
      if (nextScrollY <= 20) {
        setHidden(false)
      } else if (nextScrollY > previousScrollY.current && nextScrollY > 110) {
        setHidden(true)
      } else if (nextScrollY < previousScrollY.current) {
        setHidden(false)
      }
      previousScrollY.current = nextScrollY
    }
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const links = [
    ['/', 'Home', 'home'],
    ['/services', 'Diensten', 'services'],
    ['/overons', 'Over The Bottling Club', 'about'],
    ['/offerte', 'Contact', 'quote'],
  ]

  return <>
      <header className={`site-header ${overlay ? 'site-header--overlay' : ''} ${scrolled ? 'site-header--scrolled' : ''} ${hidden && !open ? 'site-header--hidden' : ''}`}>
      <a className="site-logo" href="/" aria-label="The Bottling Club home">
        <img src={overlay ? '/assets/logo-white.png' : '/assets/logo-black.png'} alt="The Bottling Club" />
      </a>
      <nav className="desktop-nav" aria-label="Hoofdnavigatie">
        {links.map(([href, label, key]) => <a key={href} className={active === key ? 'active' : ''} href={href}>{label}</a>)}
      </nav>
      <ButtonLink href="/thebottlingshop" className="header-shop">THE BOTTLING SHOP</ButtonLink>
      <button className="menu-toggle" aria-label={open ? 'Menu sluiten' : 'Menu openen'} aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        <span /><span /><span />
      </button>
    </header>
    <div className={`mobile-menu ${open ? 'mobile-menu--open' : ''}`} aria-hidden={!open}>
      <button className="mobile-menu__close" aria-label="Menu sluiten" onClick={() => setOpen(false)}>×</button>
      <nav aria-label="Mobiele navigatie">
        {links.map(([href, label, key]) => <a key={href} className={active === key ? 'active' : ''} href={href}>{label}</a>)}
      </nav>
    </div>
  </>
}

function Footer({ active = '' }) {
  const [year, setYear] = useState(() => new Date().getFullYear())
  useEffect(() => {
    const updateYear = () => setYear(new Date().getFullYear())
    const timer = window.setInterval(updateYear, 60 * 60 * 1000)
    return () => window.clearInterval(timer)
  }, [])
  return <footer className="footer">
    <div className="footer__grid">
      <div className="footer__brand">
        <img src="/assets/logo-black.png" alt="The Bottling Club" />
        <nav aria-label="Voettekstnavigatie">
          <a className={active === 'home' ? 'active' : ''} href="/">Home</a>
          <a className={active === 'services' ? 'active' : ''} href="/services">Diensten</a>
          <a className={active === 'about' ? 'active' : ''} href="/overons">Over The Bottling Club</a>
          <a className={active === 'quote' ? 'active' : ''} href="/offerte">Contact</a>
        </nav>
      </div>
      <div><h3>HOOFDKANTOOR</h3><p>Veldkersweg 28<br />3053 JR Rotterdam</p><p><a href="tel:+31626900525">+31 6 26 90 05 25</a><br /><a href="mailto:info@thebottlingclub.nl">info@thebottlingclub.nl</a></p></div>
      <div><h3>SOCIALS</h3><p className="footer__links"><a href="https://www.facebook.com/WixStudio">Facebook</a><a href="https://www.instagram.com/wixstudio">Instagram</a><a href="https://www.linkedin.com/company/wix-com">LinkedIn</a></p></div>
      <div><h3>OFFERTE AANVRAGEN</h3><p>Wilt u een vrijblijvende prijsopgaaf ontvangen?</p><ButtonLink href="/offerte">OFFERTE</ButtonLink></div>
    </div>
    <div className="footer__bottom">© {year} met trots gemaakt door <a href="https://www.maatwerk-marketing.nl">Maatwerk Marketing</a></div>
  </footer>
}

function Layout({ children, active, overlay = false }) {
  return <><Header overlay={overlay} active={active} /><main>{children}</main><Footer active={active} /></>
}

function useReveal() {
  useEffect(() => {
    const nodes = [...document.querySelectorAll('[data-reveal]')]
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      nodes.forEach((node) => node.classList.add('revealed'))
      return undefined
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => entry.isIntersecting && entry.target.classList.add('revealed'))
    }, { threshold: 0.15 })
    nodes.forEach((node) => observer.observe(node))
    return () => observer.disconnect()
  }, [])
}

function Portfolio() {
  const track = useRef(null)
  const move = (direction) => track.current?.scrollBy({ left: direction * track.current.clientWidth * 0.72, behavior: 'smooth' })
  return <section className="portfolio">
    <div className="section-heading split-heading">
      <div><p className="eyebrow">VAN DOP TOT DOOS</p><h2>VAN FLES TOT VLOEISTOF</h2></div>
      <span className="portfolio-label">PORTFOLIO</span>
    </div>
    <div className="portfolio__wrap">
      <div className="portfolio__track" ref={track} aria-label="Portfolio" tabIndex="0">
        {[1, 2, 3, 4].map((number) => <img key={number} src={`/assets/portfolio-${number}.avif`} alt={`Bottling Club portfolio ${number}`} />)}
      </div>
      <button className="portfolio__arrow portfolio__arrow--prev" aria-label="Vorige portfoliofoto" onClick={() => move(-1)}>←</button>
      <button className="portfolio__arrow portfolio__arrow--next" aria-label="Volgende portfoliofoto" onClick={() => move(1)}>→</button>
    </div>
  </section>
}

function HomePage() {
  useReveal()
  return <Layout active="home" overlay>
    <section className="hero">
      <img className="hero__image" src="/assets/hero.avif" alt="Twee flessen van The Bottling Club" />
      <div className="hero__content">
        <h1>Meer dan alleen een<br />bottlebedrijf</h1>
        <p>Voor productontwikkeling, productie en afvulservice van nieuwe en bestaande drankproducten.</p>
        <ButtonLink href="/thebottlingshop" light>THE BOTTLING SHOP</ButtonLink>
      </div>
    </section>

    <section className="welcome">
      <div className="welcome__copy">
        <div data-reveal>
          <p className="eyebrow">WELKOM BIJ THE BOTTLING CLUB</p>
          <h2>Jouw partner in succesvolle<br />productie</h2>
          <p>Bij The Bottling Club gaan we verder dan bottelen alleen - we bieden toegang tot een wereld vol kansen in de drankindustrie. Of u een nieuw merk opstart of een bestaand merk wilt laten groeien, wij combineren kwaliteit, innovatie en persoonlijke service om uw visie tot leven te brengen. Samen zorgen we ervoor dat uw merk perfect geproduceerd wordt, fles voor fles.</p>
          <ButtonLink href="/overons">MEER OVER ONS</ButtonLink>
        </div>
        <div className="stats">
          <div><strong>12</strong><span>Jaar Ervaring</span></div>
          <div><strong>44</strong><span>Merken Gebotteld 2024</span></div>
          <div><strong>176k</strong><span>Flessen Gebotteld 2024</span></div>
        </div>
      </div>
      <div className="welcome__visual"><img src="/assets/mockup-1.avif" alt="The Bottling Club flesmock-up" /></div>
    </section>

    <Portfolio />

    <section className="home-services">
      <div className="home-services__heading"><p className="eyebrow">ONZE DIENSTEN</p><h2>UW A TOT Z<br />OPLOSSING VOOR<br />BOTTELEN</h2></div>
      <article><Icon name="bottle" /><h3>BOTTLING</h3><p>Met toewijding aan kwaliteit, innovatie en op maat gemaakte ondersteuning maakt The Bottling Club uw ideale partner in de wereld van bottelen. Wij begrijpen dat elk merk uniek is, en daarom zetten we alles op alles om uw visie en product te realiseren. Van het eerste idee tot het eindproduct ofwel “van korrel tot borrel” – wij zorgen ervoor dat uw merk opvalt en schittert met de hoogste standaarden en aandacht</p></article>
      <article><Icon name="box" /><h3>SMALL BATCHES</h3><p>Wilt u een nieuw merk dat indruk maakt in een concurrerende markt, of een merk dat klaar is om op te schalen? Bij The Bottling Club staan we klaar om uw ambitie te realiseren. Zowel kleine als grote aantallen zijn mogelijk om af te vullen door de flexibele productiemachines.</p></article>
      <article><Icon name="tag" /><h3>PRIVATE LABEL VAN A TOT Z</h3><p>The Bottling Club biedt nieuwe en bestaande merken volledige ondersteuning bij de (her)ontwikkeling van uw product. Het adviseren, ontwikkeling van nieuwe recepturen, bestellen van goederen en het produceren van het eindproduct kunnen wij volledig in beheer nemen. Op deze wijze ontzorgen wij de klant volledig en produceren we op het gewenste moment om de voorraad op peil te houden. “Van korrel tot borrel”</p></article>
    </section>

    <section className="featured-project">
      <div className="featured-project__copy"><p className="eyebrow">UITGELICHT PROJECT</p><h2>010 VODKA</h2><dl><dt>Jaar oprichting:</dt><dd>2019</dd><dt>Inhoud:</dt><dd>Vodka</dd></dl></div>
      <img src="/assets/project-010.avif" alt="010 Vodka Zeezout" />
    </section>

    <Clients />
  </Layout>
}

function Clients() {
  const track = useRef(null)
  const move = (direction) => track.current?.scrollBy({ left: direction * track.current.clientWidth * .78, behavior: 'smooth' })
  const logos = [
    ['client-delft-blue.png', 'Delft Blue'],
    ['client-linschoten-source.png', 'Van Linschoten Slagers Gin'],
    ['client-sibble-source.png', 'Sibble'],
    ['client-bohlt-source.png', 'Bohlt'],
    ['client-mates-source.png', 'Mates Rum'],
    ['client-charlies-source.png', "Charlie's Gin"],
    ['client-zegro-source.png', 'Zegro'],
    ['client-010-vodka.png', '010 Vodka'],
    ['client-mitra.png', 'Mitra'],
  ]
  return <section className="clients">
    <div className="clients__inner">
      <p className="eyebrow">ONZE KLANTEN</p>
      <h2>Wij geloven dat elke klant een <br />waardevolle langetermijnpartner is</h2>
      <div className="clients__slider">
        <div className="clients__logos" ref={track} tabIndex="0" aria-label="Onze klanten">
          {logos.map(([src, alt]) => <img key={src} src={`/assets/${src}`} alt={alt} />)}
        </div>
        <button className="clients__arrow clients__arrow--prev" aria-label="Vorige klantlogo's" onClick={() => move(-1)}>‹</button>
        <button className="clients__arrow clients__arrow--next" aria-label="Volgende klantlogo's" onClick={() => move(1)}>›</button>
      </div>
    </div>
  </section>
}

function Icon({ name }) {
  const paths = {
    bottle: <><path d="M10 3h4v4l2 3v11H8V10l2-3z" /><path d="M10 12h4" /></>,
    box: <><path d="M4 8h16v12H4z" /><path d="m4 8 3-5h10l3 5M12 8v12M8 12l-2 2m10-2 2 2" /></>,
    tag: <><path d="M3 9l7-6h10v10l-7 7L3 10z" /><circle cx="16" cy="7" r="1" /></>,
    puzzle: <path d="M4 4h6a2 2 0 1 1 4 0h6v6a2 2 0 1 0 0 4v6h-6a2 2 0 1 0-4 0H4v-6a2 2 0 1 0 0-4z" />,
    tube: <><path d="M8 3h8M9 3v15a3 3 0 0 0 6 0V3" /><path d="M9 12h6" /></>,
    brain: <><path d="M9 4a3 3 0 0 0-4 3 3 3 0 0 0-1 5 3 3 0 0 0 3 5 3 3 0 0 0 5 2V6a2 2 0 0 0-3-2Z" /><path d="M15 4a3 3 0 0 1 4 3 3 3 0 0 1 1 5 3 3 0 0 1-3 5 3 3 0 0 1-5 2V6a2 2 0 0 1 3-2Z" /><path d="M8 9h4m0 5h4" /></>,
  }
  return <svg className="service-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" aria-hidden="true">{paths[name]}</svg>
}

function PageHero({ eyebrow, title }) {
  return <section className="page-hero"><p className="eyebrow">{eyebrow}</p><h1 data-reveal>{title}</h1></section>
}

function ServicesPage() {
  useReveal()
  return <Layout active="services">
    <PageHero eyebrow="ONZE DIENSTEN" title="Van ontwikkeling tot productie tot de markt, we hebben jouw rug." />
    <section className="services-grid">
      <div className="services-grid__items">{serviceItems.map((item) => <article key={item.title}><Icon name={item.icon} /><h2>{item.title}</h2><p>{item.text}</p></article>)}</div>
      <div className="services-grid__visual"><img src="/assets/services-010.avif" alt="010 Vodka fles" /></div>
    </section>
  </Layout>
}

function AboutPage() {
  useReveal()
  const testimonials = [
    ['De persoonlijke aanpak maakt het fijn om samen te werken en je merkt aan alles dat zij ervaring hebben', 'DE KLEIWEG GIN', 'Rotterdam'],
    ['Een professionele partij met een oplossing voor elk traject, zeker een aanrader!', 'ZEGRO', 'Delft'],
    ['Na jarenlang samen te werken hebben zij nog nooit een steekje laten vallen, top partij!', 'VAN LINSCHOTEN GIN', 'Rotterdam'],
  ]
  return <Layout active="about">
    <section className="about-intro">
      <div className="about-intro__left"><div className="about-intro__title"><p className="eyebrow">WIE ZIJN WIJ?</p><h1 data-reveal>Waarom kiezen voor<br />TBC?</h1></div><div className="about-intro__image"><img src="/assets/mockup-2.avif" alt="The Bottling Club productmock-up" /></div></div>
      <div className="about-intro__right"><p>Bij The Bottling Club zijn we meer dan alleen een bottelservice - we zijn jouw partner in het creëren van succes. Jouw merk verdient het beste, en dat is precies wat we leveren. Laten we samen aan dit spannende avontuur beginnen.</p><p>Verken onze website om meer te weten te komen over onze diensten, bekijk ons portfolio en neem contact met ons op. We kijken ernaar uit om met jou samen te werken en jouw merk naar nieuwe hoogten te brengen.</p><div className="about-points"><h2>KWALITEITSGARANTIES</h2><p>Onze toewijding aan kwaliteit is onwankelbaar. Jouw product voldoet aan de hoogste normen in elke fles.</p><h2>INNOVATIE</h2><p>We blijven op de hoogte van de nieuwste trends in de branche, zodat jouw merk competitief en relevant blijft.</p><h2>PERSOONLIJKE ONDERSTEUNING</h2><p>We staan klaar om jouw vragen te beantwoorden, begeleiding te bieden en jouw partner te zijn in succes.</p></div></div>
    </section>
    <section className="testimonials"><div className="testimonials__head"><p className="eyebrow">TESTIMONIALS</p><h2>Verhalen van klanten die u <br />voor zijn gegaan:</h2></div><div className="testimonials__grid">{testimonials.map(([quote, name, city]) => <article key={name}><div className="stars" aria-label="Vijf sterren">☆ ☆ ☆ ☆ ☆</div><p>{quote}</p><strong>{name}</strong><span>{city}</span></article>)}</div></section>
    <section className="mission"><div className="mission__copy"><div><p className="eyebrow">ONZE MISSIE</p><h2>Ondersteuning Gedurende <br />Jouw Hele Reis</h2><p>Het creëren van een nieuw merk of het lanceren van een klein merk is geen kleine prestatie. Daarom bieden we meer dan alleen een bottelservice. We bieden uitgebreide ondersteuning en begeleiden je bij elke stap van het proces. Van het verfijnen van je recept tot het ontwerpen van labels die het verhaal van jouw merk vertellen, staan we klaar om ervoor te zorgen dat jouw product opvalt in de markt.</p></div></div><div className="mission__image"><img src="/assets/mockup-3.avif" alt="The Bottling Club flesmock-up" /></div></section>
  </Layout>
}

async function postForm(form, kind) {
  const endpoint = import.meta.env.VITE_FORMS_ENDPOINT || '/api/forms'
  const body = Object.fromEntries(new FormData(form).entries())
  if (body._website) return { ok: true }
  delete body._website

  const subject = kind === 'product'
    ? `Productaanvraag: ${body.product} via thebottlingclub.nl`
    : 'Nieuwe offerteaanvraag via thebottlingclub.nl'
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 20_000)

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      signal: controller.signal,
      body: JSON.stringify({
        ...body,
        _subject: subject,
        _template: 'table',
        _replyto: body.email,
        _honey: '',
      }),
    })
    const data = await response.json().catch(() => ({}))
    const delivered = data.success === true || String(data.success).toLowerCase() === 'true'
    if (!response.ok || !delivered) {
      const activationNeeded = /activat/i.test(String(data.message || ''))
      throw new Error(activationNeeded
        ? 'Het formulier moet nog eenmalig via de bevestigingsmail worden geactiveerd.'
        : 'Uw aanvraag kon niet worden verzonden. Probeer het later opnieuw.')
    }
    return data
  } catch (error) {
    if (error.name === 'AbortError') throw new Error('Het verzenden duurde te lang. Probeer het opnieuw.', { cause: error })
    throw error
  } finally {
    window.clearTimeout(timeout)
  }
}

function useFormState(kind) {
  const [status, setStatus] = useState({ type: 'idle', message: '' })
  const busy = useRef(false)
  const submit = async (event) => {
    event.preventDefault()
    if (busy.current) return
    const form = event.currentTarget
    if (!form.reportValidity()) return
    busy.current = true
    setStatus({ type: 'loading', message: 'Uw aanvraag wordt verzonden…' })
    try {
      await postForm(form, kind)
      form.reset()
      setStatus({ type: 'success', message: 'Bedankt! Uw aanvraag is ontvangen. We nemen zo snel mogelijk contact met u op.' })
    } catch (error) {
      setStatus({ type: 'error', message: error.message })
    } finally {
      busy.current = false
    }
  }
  return [status, submit]
}

function FormStatus({ status }) {
  if (status.type === 'idle') return null
  return <p className={`form-status form-status--${status.type}`} role="status">{status.message}</p>
}

function QuotePage() {
  useReveal()
  const [status, submit] = useFormState('quote')
  return <Layout active="quote">
    <section className="quote-main">
      <div className="quote-main__form"><p className="eyebrow">VRAAG EEN VRIJBLIJVENDE OFFERTE AAN</p><h1 data-reveal>Laat ons uw wensen weten <br />en wij gaan aan de slag!</h1><p>U ontvangt zo snel mogelijk van ons een terugkoppeling</p>
        <form className="form" onSubmit={submit} noValidate>
          <label>Contactpersoon *<input name="contactpersoon" required autoComplete="name" /></label>
          <label>Bedrijfsnaam *<input name="bedrijfsnaam" required autoComplete="organization" /></label>
          <label>Email *<input name="email" type="email" required autoComplete="email" /></label>
          <label>Telefoonnummer<input name="telefoonnummer" type="tel" autoComplete="tel" /></label>
          <label>Welke dienst heeft u nodig?<select name="dienst" defaultValue=""><option value="" disabled>Kies een optie</option><option>Botteling</option><option>Small Batches</option><option>Private Label</option><option>Consult / Agency</option><option>A tot Z</option></select></label>
          <label>Aantal flessen<input name="aantal_flessen" inputMode="numeric" /></label>
          <label className="form__full">Beschrijving<textarea name="beschrijving" rows="4" /></label>
          <label className="honeypot" aria-hidden="true">Website<input name="_website" tabIndex="-1" autoComplete="off" /></label>
          <button className="form__submit" disabled={status.type === 'loading'}>{status.type === 'loading' ? 'Verzenden…' : 'Verstuur mijn aanvraag >'}</button>
          <FormStatus status={status} />
        </form>
      </div>
      <div className="quote-main__visual"><img src="/assets/quote-visual.avif" alt="The Bottling Club verpakkingsmock-up" /></div>
    </section>
    <ContactBand />
  </Layout>
}

function ContactBand() {
  return <section className="contact-band"><p className="eyebrow">Neem contact op</p><h2>Liever gelijk in contact komen met ons?</h2><div className="contact-band__grid"><div><strong>ADRES</strong><span>Weg en Land 39a<br />2661 DC, Bergschenhoek</span></div><div><strong>OPENINGSTIJDEN</strong><span>Maandag - Zaterdag:<br />09:00 - 17:00</span></div><div><strong>EMAIL</strong><a href="mailto:info@thebottlingclub.nl">info@thebottlingclub.nl</a></div><div><strong>TELEFOON</strong><a href="tel:+31626900525">+31 6 26 90 05 25</a></div></div></section>
}

function ShopPage() {
  return <Layout>
    <section className="shop"><img className="shop__banner" src="/assets/shop-banner.avif" alt="The Bottling Club productverpakking" /><h1>Bekijk hier onze producten</h1><div className="product-grid">{products.map((product) => <article key={product.slug}><a href={`/items/${product.slug}`}><img src={product.card} alt={product.title} /></a><h2>{product.title}</h2><a className="product-grid__button" href={`/items/${product.slug}`}>Bekijken</a></article>)}</div></section>
  </Layout>
}

function SelectField({ label, name, options }) {
  return <label>{label}<select name={name} defaultValue=""><option value="" disabled>Kies een optie</option>{options.map((option) => <option key={option}>{option}</option>)}</select></label>
}

function ProductPage({ product }) {
  const [status, submit] = useFormState('product')
  const [imageIndex, setImageIndex] = useState(0)
  const currentIndex = products.findIndex((item) => item.slug === product.slug)
  const previous = products[(currentIndex - 1 + products.length) % products.length]
  const next = products[(currentIndex + 1) % products.length]
  const productAssetName = product.slug
  const images = [`/assets/product-${productAssetName}-1.png`, `/assets/product-${productAssetName}-2.png`, '/assets/product-options.png']
  return <Layout>
    <section className="product-page">
      <div className="product-title"><div className="product-title__inner"><h1>{product.title}</h1></div></div>
      <div className="product-config">
        <div className="product-gallery"><div className="product-gallery__main"><img src={images[imageIndex]} alt={`${product.title} productafbeelding ${imageIndex + 1}`} /><button aria-label="Volgende productafbeelding" onClick={() => setImageIndex((imageIndex + 1) % images.length)}>›</button></div><div className="product-gallery__thumbs">{images.map((image, index) => <button key={image} aria-label={`Toon afbeelding ${index + 1}`} className={index === imageIndex ? 'active' : ''} onClick={() => setImageIndex(index)}><img src={image} alt="" /></button>)}</div></div>
        <form className="product-form" onSubmit={submit} noValidate><h2>Vul hier jouw wensen in</h2><input type="hidden" name="product" value={product.title} />
          <SelectField label="Formaat" name="formaat" options={productOptions.formaat} />
          <SelectField label="Aantallen" name="aantallen" options={productOptions.aantallen} />
          <SelectField label="Etiket" name="etiket" options={productOptions.etiket} />
          <SelectField label="Etiket vorm" name="etiket_vorm" options={productOptions.etiketVorm} />
          <SelectField label="Kaart vorm (indien van toepassing)" name="kaart_vorm" options={productOptions.kaartVorm} />
          <SelectField label="Dopsticker" name="dopsticker" options={productOptions.dopsticker} />
          <h2 className="product-form__subhead">Gegevens</h2>
          <label>Voornaam *<input name="voornaam" required autoComplete="name" placeholder="Voornaam" /></label>
          <label>Bedrijf<input name="bedrijf" autoComplete="organization" /></label>
          <label>E-mailadres *<input name="email" type="email" required autoComplete="email" placeholder="E-mailadres" /></label>
          <label><span className="sr-only">Opmerkingen</span><textarea name="opmerkingen" rows="1" placeholder="Plaats hier uw eventuele opmerkingen" /></label>
          <label className="honeypot" aria-hidden="true">Website<input name="_website" tabIndex="-1" autoComplete="off" /></label>
          <button className="form__submit" disabled={status.type === 'loading'}>{status.type === 'loading' ? 'Verzenden…' : 'Vraag vrijblijvende offerte aan'}</button>
          <FormStatus status={status} />
        </form>
      </div>
      <ProductInfo />
      <div className="product-nav"><a href={`/items/${previous.slug}`}>Vorige</a><a href={`/items/${next.slug}`}>Volgende</a></div>
    </section>
  </Layout>
}

function ProductInfo() {
  return <section className="accordions">
    <details open><summary>Voorwaarden</summary><p><a href="/av">Bekijk hier onze algemene voorwaarden</a></p></details>
    <details><summary>Aanleveren</summary><p><a href="/aanleverspecificaties">Klik hier om de aanleverspecificaties te bekijken of te downloaden</a></p></details>
    <details><summary>Prijzen</summary><p>Prijs op aanvraag. Vul hierboven de gewenste informatie in en ontvang binnen 1-2 werkdagen een vrijblijvende offerte.</p></details>
    <details><summary>Levering</summary><p>Levertijden 14 - 21 dagen <strong>na akkoord ontwerp</strong> klant. Afhankelijk van de wensen van de klant in overleg</p></details>
  </section>
}

function LegalPage() {
  return <Layout><article className="legal"><h1>Algemene Voorwaarden The Bottling Club BV</h1><p>Versie: 1.0, Datum: 3 september 2025</p>{legalSections.map(([title, paragraphs]) => <section key={title}><h2>{title}</h2>{title === 'Artikel 1 - Definities' && <p>In deze algemene voorwaarden wordt verstaan onder:</p>}<ol>{paragraphs.map((paragraph) => <li key={paragraph}>{paragraph}</li>)}</ol></section>)}<section><h2>Contactgegevens</h2><p>The Bottling Club BV<br />Weg en Land 39A<br />2661 DC, Bergschenhoek<br />E-mail: <a href="mailto:info@thebottlingclub.nl">info@thebottlingclub.nl</a><br />Website: <a href="/">www.thebottlingclub.nl</a></p></section></article></Layout>
}

function SpecsPage() {
  const docs = [
    ['/assets/Aanleverspecificaties-TBC.pdf', '/assets/aanleverspecificaties-etiketten.png', 'Aanleverspecificaties etiketten'],
    ['/assets/Aanleverspecificaties-TBC-2.pdf', '/assets/aanleverspecificaties-bedrukking.png', 'Aanleverspecificaties bedrukking'],
  ]
  return <Layout><section className="specs"><h1 className="sr-only">Aanleverspecificaties</h1>{docs.map(([pdf, image, alt]) => <a key={pdf} href={pdf} download><img src={image} alt={alt} /><span><b>PDF</b>Klik op het PDF document om<br />het bestand te downloaden</span></a>)}</section></Layout>
}

function NotFoundPage() {
  return <Layout><section className="not-found"><p className="eyebrow">404</p><h1>Deze pagina bestaat niet.</h1><ButtonLink href="/">TERUG NAAR HOME</ButtonLink></section></Layout>
}

export default function App() {
  const route = useMemo(() => resolveRoute(currentPath()), [])
  useEffect(() => {
    window.scrollTo(0, 0)
    const titles = { home: 'The Bottling Club | Home', services: 'The Bottling Club | Diensten', about: 'The Bottling Club | Over Ons', quote: 'The Bottling Club | Offerte Aanvragen', shop: 'Items (List) | The Bottling Club', legal: 'Algemene Voorwaarden | The Bottling Club', specs: 'Aanleverspecificaties | The Bottling Club' }
    document.title = route.page === 'product' ? `${route.product.title} | The Bottling Club` : (titles[route.page] || 'The Bottling Club')
  }, [route])

  if (route.page === 'home') return <HomePage />
  if (route.page === 'services') return <ServicesPage />
  if (route.page === 'about') return <AboutPage />
  if (route.page === 'quote') return <QuotePage />
  if (route.page === 'shop') return <ShopPage />
  if (route.page === 'product') return <ProductPage product={route.product} />
  if (route.page === 'legal') return <LegalPage />
  if (route.page === 'specs') return <SpecsPage />
  return <NotFoundPage />
}
